import React from 'react';
import { getRiskColor, getMIAColor } from './tokens';
import { useTheme } from './ThemeContext';
import { IconCheck, IconX } from './Icons';
import { Product } from '../types/products';

// ============================================================
// ANIMATED SVG COMPONENTS
// ============================================================

export const IRemedyCross = ({ size = 40 }: { size?: number }) => (
  <img src="/tradespy-logo.png" alt="TradeSpy.ai" width={size} height={size} style={{ display: 'block' }} />
);

export const FloatingPill = ({ delay = 0, x = 0, color }: { delay?: number; x?: number | string; color?: string }) => {
  const { T } = useTheme();
  const c = color || T.gold;
  return (
  <div style={{ position: "absolute", left: x, top: 0, animation: `floatPill 6s ease-in-out ${delay}s infinite`, opacity: 0.12 }}>
    <svg width="32" height="16" viewBox="0 0 32 16">
      <rect x="0" y="0" width="32" height="16" rx="8" fill={c}/>
      <rect x="16" y="0" width="16" height="16" rx="8" fill={T.navy} opacity="0.5"/>
    </svg>
  </div>
  );
};

export const GlobeIcon = ({ size = 120, animated = true }: { size?: number; animated?: boolean }) => {
  const { T } = useTheme();
  return (
  <svg width={size} height={size} viewBox="0 0 120 120" style={animated ? { animation: "spinSlow 20s linear infinite" } : {}}>
    <circle cx="60" cy="60" r="54" fill="none" stroke={T.navy} strokeWidth="1.5" opacity="0.15"/>
    <ellipse cx="60" cy="60" rx="30" ry="54" fill="none" stroke={T.navy} strokeWidth="1" opacity="0.1"/>
    <ellipse cx="60" cy="60" rx="54" ry="20" fill="none" stroke={T.navy} strokeWidth="1" opacity="0.1"/>
    <path d="M6 60 H114" stroke={T.navy} strokeWidth="0.5" opacity="0.08"/>
    <path d="M60 6 V114" stroke={T.navy} strokeWidth="0.5" opacity="0.08"/>
    <circle cx="60" cy="60" r="54" fill="none" stroke={T.gold} strokeWidth="2" strokeDasharray="8 4" opacity="0.2"/>
  </svg>
  );
};

// ============================================================
// UI COMPONENTS
// ============================================================

export const ScoreRing = ({ score, size = 56, strokeWidth = 4, color, label }: { score: number; size?: number; strokeWidth?: number; color?: string; label?: string }) => {
  const { T } = useTheme();
  const r = (size - strokeWidth) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;
  const c = color || getMIAColor(score);
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={T.border} strokeWidth={strokeWidth} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={c} strokeWidth={strokeWidth} strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" style={{ transition: "stroke-dashoffset 0.8s ease" }} />
      </svg>
      <span style={{ position: "relative", marginTop: -(size/2 + 8), fontSize: size > 50 ? 15 : 11, fontWeight: 700, color: c }}>{score}%</span>
      {label && <span style={{ fontSize: 10, color: T.textLight, marginTop: size > 50 ? 14 : 10, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>{label}</span>}
    </div>
  );
};

export const StatCard = ({ title, value, subtitle, color, icon, accent }: { title: string; value: string | number; subtitle?: string; color?: string; icon?: React.ReactNode; accent?: string }) => {
  const { T } = useTheme();
  return (
  <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: "20px 24px", display: "flex", flexDirection: "column", gap: 8, boxShadow: T.shadow, borderTop: accent ? `3px solid ${accent}` : "none", transition: "box-shadow 0.2s", position: "relative", overflow: "hidden", height: "100%" }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <span style={{ fontSize: 11, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700 }}>{title}</span>
      {icon && <span style={{ color: color || T.textLight }}>{icon}</span>}
    </div>
    <span style={{ fontSize: 32, fontWeight: 800, color: color || T.navy, letterSpacing: "-0.03em" }}>{value}</span>
    {subtitle && <span style={{ fontSize: 12, color: T.textMid }}>{subtitle}</span>}
  </div>
  );
};

export const RiskBadge = ({ risk }: { risk: string }) => {
  const c = getRiskColor(risk);
  return <span style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}`, borderRadius: 20, padding: "4px 14px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>{risk}</span>;
};


// Source badge component - displays data provenance
export const SourceBadge = ({ source, confidence, scraped_at, compact = false }: { source?: string; confidence?: string; scraped_at?: string; compact?: boolean }) => {
  const { T } = useTheme();
  if (!source) return null;

  // Source configuration with colors and labels
  const sourceConfig: { [key: string]: { label: string; color: string; bg: string; icon: string } } = {
    FDA_NDC: { label: "FDA NDC", color: "#0369A1", bg: "#F0F9FF", icon: "\u{1F3DB}\u{FE0F}" },
    FDA_SHORTAGE: { label: "FDA Shortage", color: "#B45309", bg: "#FFFBEB", icon: "\u{26A0}\u{FE0F}" },
    CMS_NADAC: { label: "CMS NADAC", color: "#059669", bg: "#ECFDF5", icon: "\u{1F4B0}" },
    RXNORM: { label: "RxNorm", color: "#7C3AED", bg: "#F5F3FF", icon: "\u{1F9EC}" },
    DAILYMED: { label: "DailyMed", color: "#DC2626", bg: "#FEF2F2", icon: "\u{1F4C4}" },
    VISION_API: { label: "Vision API", color: "#0891B2", bg: "#ECFEFF", icon: "\u{1F441}\u{FE0F}" },
    MANUAL: { label: "Manual", color: "#64748B", bg: "#F8FAFC", icon: "\u{270F}\u{FE0F}" },
  };

  const config = sourceConfig[source] || { label: source, color: "#64748B", bg: "#F8FAFC", icon: "\u{1F4CA}" };

  // Confidence badge
  const confColor = confidence === "high" ? T.success : confidence === "medium" ? T.warning : confidence === "low" ? T.danger : T.textLight;

  // Format date
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  if (compact) {
    return (
      <span
        title={`Source: ${config.label}${confidence ? ` | Confidence: ${confidence}` : ""}${scraped_at ? ` | Updated: ${formatDate(scraped_at)}` : ""}`}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 3,
          background: config.bg,
          color: config.color,
          borderRadius: 10,
          padding: "2px 8px",
          fontSize: 9,
          fontWeight: 600,
          cursor: "help",
        }}
      >
        <span style={{ fontSize: 10 }}>{config.icon}</span>
        {config.label}
      </span>
    );
  }

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        background: config.bg,
        border: `1px solid ${config.color}30`,
        borderRadius: 6,
        padding: "4px 10px",
        fontSize: 10,
      }}
    >
      <span style={{ fontSize: 12 }}>{config.icon}</span>
      <span style={{ fontWeight: 700, color: config.color }}>{config.label}</span>
      {confidence && (
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: confColor }} title={`Confidence: ${confidence}`} />
      )}
      {scraped_at && (
        <span style={{ color: T.textLight, fontSize: 9 }}>{formatDate(scraped_at)}</span>
      )}
    </div>
  );
};

// Source indicator for section headers
export const SectionSourceIndicator = ({ sources, fields }: { sources?: Product["data_sources"]; fields: string[] }) => {
  if (!sources) return null;

  // Find the most relevant source for these fields
  const relevantSources = fields.map(f => sources[f]).filter(Boolean);
  if (relevantSources.length === 0) return null;

  // Group by source - use Array.from for TypeScript compatibility
  const uniqueSources = Array.from(new Set(relevantSources.map(s => s.source)));
  const sourceHref = (source: string) => `#coo-section-source_${encodeURIComponent(source)}`;

  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {uniqueSources.map(source => {
        const sourceData = relevantSources.find(s => s.source === source);
        return (
          <a
            key={source}
            href={sourceHref(source)}
            onClick={() => {
              window.dispatchEvent(new CustomEvent("coo-open-target", {
                detail: { hash: `coo-section-source_${encodeURIComponent(source)}` }
              }));
            }}
            style={{ textDecoration: "none" }}
            title={`Open source details: ${source}`}
          >
            <SourceBadge source={source} confidence={sourceData?.confidence} scraped_at={sourceData?.scraped_at} compact />
          </a>
        );
      })}
    </div>
  );
};

export const SectionHeader = ({ children, sub }: { children: React.ReactNode; sub?: string }) => {
  const { T } = useTheme();
  return (
  <div style={{ marginBottom: 4 }}>
    <h2 style={{ fontSize: 24, fontWeight: 800, color: T.navy, letterSpacing: "-0.02em", lineHeight: 1.2 }}>{children}</h2>
    {sub && <p style={{ fontSize: 13, color: T.textLight, marginTop: 6, lineHeight: 1.5 }}>{sub}</p>}
  </div>
  );
};
